import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  ChevronRight,
  FolderPlus,
  ImagePlus,
  MoreHorizontal,
  Search,
  SquareCheck,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import {
  GetAdminV1OrgMediaMimeType,
  GetAdminV1OrgMediaSort,
  MediaKind,
  type Media,
  type MediaFolder,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { DeleteFolderDialog } from '../components/delete-folder-dialog';
import { FolderNameDialog } from '../components/folder-name-dialog';
import { FolderPickerDialog } from '../components/folder-picker-dialog';
import { FolderTiles } from '../components/folder-tiles';
import { MediaDetailSheet } from '../components/media-detail-sheet';
import { MediaGrid } from '../components/media-grid';
import { MediaPickerDialog } from '../components/media-picker-dialog';
import { MediaUploadButton } from '../components/media-upload-button';
import { UploadQueueList } from '../components/upload-queue-list';
import {
  useAddToMediaFolders,
  useRemoveFromMediaFolder,
} from '../hooks/use-media-folder-mutations';
import { useMediaFolders } from '../hooks/use-media-folders';
import { useMediaList } from '../hooks/use-media-list';
import { useMediaUploader } from '../hooks/use-media-uploader';
import { mediaLibraryParams, UNFILED, type MediaListSearch } from '../schemas/media-list-search';

const ROUTE_ID = '/_authed/_active/org/media' as const;
const ALL = '__all__';

const KINDS = Object.values(MediaKind);
const MIME_TYPES = Object.values(GetAdminV1OrgMediaMimeType);
const SORTS = Object.values(GetAdminV1OrgMediaSort);

const isKind = (v: string): v is MediaKind => (KINDS as string[]).includes(v);
const isMimeType = (v: string): v is GetAdminV1OrgMediaMimeType =>
  (MIME_TYPES as string[]).includes(v);
const isSort = (v: string): v is GetAdminV1OrgMediaSort => (SORTS as string[]).includes(v);

function MediaLibrary() {
  const { t } = useTranslation(['media', 'common']);
  const { can } = usePermissions();
  const canWrite = can('media.write');
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/org/media' });
  const [openMedia, setOpenMedia] = useState<Media | null>(null);
  const [text, setText] = useState(search.q ?? '');
  const typed = useDebouncedValue(text.trim(), 300);
  // Only a new debounced value writes q, so Clear is not undone by the still-pending old text.
  const pushed = useRef(search.q ?? '');

  const patch = (next: Partial<MediaListSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true });

  useEffect(() => {
    if (typed === pushed.current) return;
    pushed.current = typed;
    void navigate({
      search: (prev) => ({ ...prev, q: typed === '' ? undefined : typed }),
      replace: true,
    });
  }, [navigate, typed]);

  // Folder state: the URL names the open folder; the folder list gives it a name and a count.
  const folders = useMediaFolders();
  const folderParam = search.folder;
  const unfiled = folderParam === UNFILED;
  const currentFolder: MediaFolder | null =
    folderParam && !unfiled ? (folders.data?.find((f) => f.id === folderParam) ?? null) : null;
  const inFolder = Boolean(folderParam);
  const currentFolderId = currentFolder?.id;

  const [createOpen, setCreateOpen] = useState(false);
  const [renameFolder, setRenameFolder] = useState<MediaFolder | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<MediaFolder | null>(null);
  const [addFilesOpen, setAddFilesOpen] = useState(false);
  const [addToFoldersOpen, setAddToFoldersOpen] = useState(false);

  // Selection: "Select" turns tiles into checkboxes for "Add to folder" / "Remove from folder".
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelected = (media: Media) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(media.id)) next.delete(media.id);
      else next.add(media.id);
      return next;
    });
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };

  const addToFolders = useAddToMediaFolders();
  const addUploads = useAddToMediaFolders({ silent: true });
  const removeFromFolder = useRemoveFromMediaFolder();

  // A file uploaded while a folder is open lands in that folder, as it would in a file manager.
  const onUploaded = useCallback(
    (media: Media) => {
      if (currentFolderId)
        addUploads.mutate({ folderIds: [currentFolderId], mediaIds: [media.id] });
    },
    // The mutation object is stable per render of the hook; only the folder matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFolderId],
  );
  const uploads = useMediaUploader({ onUploaded });

  const query = useMediaList(mediaLibraryParams(search));
  const items = query.data?.pages.flatMap((p) => p.items);
  const narrowed = [search.q, search.kind, search.mimeType, search.missingAlt].some(Boolean);

  const openFolder = (folder: string | undefined) => {
    stopSelecting();
    patch({ folder });
  };

  const uploadButton = (tour?: string) => (
    <Can permission="media.write">
      <MediaUploadButton onFiles={uploads.add} data-tour={tour}>
        {t('media:upload.action')}
      </MediaUploadButton>
    </Can>
  );

  const folderTitle = unfiled
    ? t('media:folders.unfiled')
    : (currentFolder?.name ?? (folders.isPending ? '' : t('media:folders.unknown')));

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      {currentFolder ? (
        <Can permission="media.write">
          <Button variant="outline" onClick={() => setAddFilesOpen(true)}>
            <ImagePlus aria-hidden />
            {t('media:folders.addFiles')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label={t('media:folders.actions', { name: currentFolder.name })}
              >
                <MoreHorizontal aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40">
              <DropdownMenuItem onSelect={() => setRenameFolder(currentFolder)}>
                {t('media:folders.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => setDeleteFolder(currentFolder)}
              >
                {t('media:folders.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Can>
      ) : inFolder ? null : (
        <Can permission="media.write">
          <Button variant="outline" onClick={() => setCreateOpen(true)}>
            <FolderPlus aria-hidden />
            {t('media:folders.new')}
          </Button>
        </Can>
      )}
      {uploadButton('media.library.upload-button')}
    </div>
  );

  const emptyFolderState =
    !narrowed && inFolder && items?.length === 0 ? (
      <p className="text-muted-foreground py-10 text-center text-sm">
        {unfiled ? t('media:folders.unfiledEmpty') : t('media:folders.emptyFolder')}
      </p>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('media:title')}
        description={t('media:description')}
        actions={headerActions}
      />
      <div className="flex flex-col gap-4">
        <nav aria-label={t('media:folders.breadcrumb')} className="flex items-center gap-1 text-sm">
          {inFolder ? (
            <>
              <Button
                type="button"
                variant="link"
                className="h-auto px-0"
                onClick={() => openFolder(undefined)}
              >
                {t('media:folders.all')}
              </Button>
              <ChevronRight className="text-muted-foreground size-4" aria-hidden />
              <span className="font-medium" aria-current="page">
                {folderTitle}
              </span>
              {currentFolder ? (
                <span className="text-muted-foreground">
                  · {t('media:folders.count', { count: currentFolder.itemCount })}
                </span>
              ) : null}
            </>
          ) : (
            <span className="font-medium" aria-current="page">
              {t('media:folders.all')}
            </span>
          )}
        </nav>
        {!inFolder && !narrowed ? (
          <FolderTiles
            folders={folders.data}
            isLoading={folders.isPending}
            onOpen={openFolder}
            onCreate={canWrite ? () => setCreateOpen(true) : undefined}
            onRename={canWrite ? setRenameFolder : undefined}
            onDelete={canWrite ? setDeleteFolder : undefined}
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-2" data-tour="media.library.filters">
          <div className="relative w-full sm:w-64" data-tour="media.library.search">
            <Search
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              value={text}
              maxLength={120}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('media:filters.searchPlaceholder')}
              aria-label={t('media:filters.search')}
              className="pl-8"
            />
          </div>
          <Select
            value={search.kind ?? ALL}
            onValueChange={(value) => patch({ kind: isKind(value) ? value : undefined })}
          >
            <SelectTrigger className="w-[160px]" aria-label={t('media:filters.kind')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('media:filters.allKinds')}</SelectItem>
              {KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {t(`media:kind.${kind}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={search.mimeType ?? ALL}
            onValueChange={(value) => patch({ mimeType: isMimeType(value) ? value : undefined })}
          >
            <SelectTrigger className="w-[180px]" aria-label={t('media:filters.mimeType')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('media:filters.allTypes')}</SelectItem>
              {MIME_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={search.sort ?? 'newest'}
            onValueChange={(value) =>
              patch({ sort: isSort(value) && value !== 'newest' ? value : undefined })
            }
          >
            <SelectTrigger className="w-[170px]" aria-label={t('media:filters.sort')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((sort) => (
                <SelectItem key={sort} value={sort}>
                  {t(`media:sort.${sort}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 px-1" data-tour="media.library.missing-alt">
            <Switch
              id="media-missing-alt"
              checked={search.missingAlt ?? false}
              onCheckedChange={(checked) => patch({ missingAlt: checked ? true : undefined })}
            />
            <Label htmlFor="media-missing-alt" className="text-sm font-normal">
              {t('media:filters.missingAlt')}
            </Label>
          </div>
          {narrowed ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setText('');
                void navigate({
                  search: (prev) => ({
                    ...(prev.sort ? { sort: prev.sort } : {}),
                    ...(prev.folder ? { folder: prev.folder } : {}),
                  }),
                  replace: true,
                });
              }}
            >
              {t('media:filters.clear')}
            </Button>
          ) : null}
          {items ? (
            <span className="text-muted-foreground text-sm">
              {t('media:count', { count: items.length })}
              {query.hasNextPage ? '+' : ''}
            </span>
          ) : null}
          {canWrite && !selecting && items && items.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => setSelecting(true)}
            >
              <SquareCheck aria-hidden />
              {t('media:folders.select')}
            </Button>
          ) : null}
        </div>
        {selecting ? (
          <div
            className="bg-muted/40 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
            role="toolbar"
            aria-label={t('media:folders.selectionToolbar')}
          >
            <span className="text-sm" aria-live="polite">
              {t('media:folders.selected', { count: selected.size })}
            </span>
            <Button
              type="button"
              size="sm"
              disabled={selected.size === 0}
              onClick={() => setAddToFoldersOpen(true)}
            >
              {t('media:folders.addToFolder')}
            </Button>
            {currentFolder ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={selected.size === 0 || removeFromFolder.isPending}
                onClick={() =>
                  removeFromFolder.mutate(
                    { folderId: currentFolder.id, mediaIds: [...selected] },
                    { onSuccess: stopSelecting },
                  )
                }
              >
                {t('media:folders.removeFromFolder')}
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={stopSelecting}
            >
              {t('media:folders.done')}
            </Button>
          </div>
        ) : null}
        <UploadQueueList
          items={uploads.items}
          onCancel={uploads.cancel}
          onRemove={uploads.remove}
          onClearFinished={uploads.clearFinished}
        />
        {narrowed && items?.length === 0 ? (
          <p className="text-muted-foreground py-10 text-center text-sm">
            {t('media:filters.noMatches')}
          </p>
        ) : (
          (emptyFolderState ?? (
            <MediaGrid
              items={items}
              isLoading={query.isPending}
              error={query.error}
              onRetry={() => void query.refetch()}
              onSelect={selecting ? toggleSelected : setOpenMedia}
              selectedIds={selecting ? selected : undefined}
              hasMore={query.hasNextPage}
              isFetchingMore={query.isFetchingNextPage}
              onLoadMore={() => void query.fetchNextPage()}
              emptyAction={uploadButton()}
              tour
            />
          ))
        )}
      </div>
      <MediaDetailSheet media={openMedia} onClose={() => setOpenMedia(null)} />
      <FolderNameDialog
        key={`create-${String(createOpen)}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={(folder) => openFolder(folder.id)}
      />
      <FolderNameDialog
        key={`rename-${renameFolder?.id ?? 'none'}`}
        open={renameFolder !== null}
        onOpenChange={(open) => !open && setRenameFolder(null)}
        folder={renameFolder}
      />
      <DeleteFolderDialog
        folder={deleteFolder}
        onOpenChange={(open) => !open && setDeleteFolder(null)}
        onDeleted={(id) => {
          if (folderParam === id) openFolder(undefined);
        }}
      />
      <FolderPickerDialog
        open={addToFoldersOpen}
        onOpenChange={setAddToFoldersOpen}
        title={t('media:folders.pickTitle', { count: selected.size })}
        description={t('media:folders.pickDescription')}
        confirmLabel={t('media:folders.confirmAdd')}
        pending={addToFolders.isPending}
        onConfirm={(folderIds) => {
          if (folderIds.length === 0) {
            setAddToFoldersOpen(false);
            return;
          }
          addToFolders.mutate(
            { folderIds, mediaIds: [...selected] },
            {
              onSuccess: () => {
                setAddToFoldersOpen(false);
                stopSelecting();
              },
            },
          );
        }}
      />
      {currentFolder ? (
        <MediaPickerDialog
          open={addFilesOpen}
          onOpenChange={setAddFilesOpen}
          kind={null}
          excludeIds={new Set(items?.map((m) => m.id))}
          onPick={(picked) =>
            addToFolders.mutate({
              folderIds: [currentFolder.id],
              mediaIds: picked.map((m) => m.id),
            })
          }
        />
      ) : null}
    </PageContent>
  );
}

export function MediaLibraryPage() {
  return (
    <FeatureErrorBoundary name="media.library">
      <MediaLibrary />
    </FeatureErrorBoundary>
  );
}
